import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Card,
  CardBody,
  Skeleton,
  Select,
  SelectItem,
} from '@nextui-org/react';
import useDataStore from '../../../../common/src/stores/useDataStore';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';
import useUserStore from '../../../../common/src/stores/useUserStore';
import {
  DashboardVstaMetricRow,
  getDashboardVstaMetrics,
} from 'common/src/api/settlements';
import ComparativeBarChart from '../../sections/settlements/comparative-seasons/ComparativeBarChart';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import ChartDownloadButton from '../../../../common/src/components/charts/ChartDownloadButton';

type MetricType = 'sales' | 'volume' | 'fob';

const VSTAWidget = () => {
  const { t } = useTranslation('dashboard');
  const { selectedSpecies } = useDataStore();
  const { selectedSeason } = useFilterStore();
  const { userProfile } = useUserStore();
  const [loading, setLoading] = useState(true);
  const [metricRows, setMetricRows] = useState<DashboardVstaMetricRow[]>([]);
  const [varieties, setVarieties] = useState<string[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('sales');
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [selectedVariety, setSelectedVariety] = useState<string>('all');

  // Calculate previous season
  const previousSeason = useMemo(() => {
    if (!selectedSeason) return '';
    const [startYear, endYear] = selectedSeason.split('-').map(Number);
    return `${startYear - 1}-${endYear - 1}`;
  }, [selectedSeason]);

  // Fetch detail-derived metrics for both seasons.
  useEffect(() => {
    const fetchData = async () => {
      if (
        !selectedSpecies?.id ||
        !selectedSeason ||
        !userProfile?.exporter_id ||
        !previousSeason
      ) {
        setMetricRows([]);
        setVarieties([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const data = await getDashboardVstaMetrics(
          userProfile.exporter_id,
          selectedSeason,
          previousSeason,
          selectedSpecies.id
        );

        setMetricRows(data?.rows || []);
        setVarieties(data?.varieties || []);
      } catch (error) {
        console.error('Error fetching VS TA data:', error);
        setMetricRows([]);
        setVarieties([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    selectedSpecies?.id,
    selectedSeason,
    previousSeason,
    userProfile?.exporter_id,
  ]);

  // Get generic week from full week string
  const getGenericWeek = (fullWeek: string | undefined): string | undefined => {
    if (!fullWeek) return undefined;
    const parts = fullWeek.split('-');
    return `W${parts[parts.length - 1].replace('W', '')}`;
  };

  // Sort weeks in fiscal order
  const sortWeeksCustom = (a: string, b: string): number => {
    const numA = parseInt(a.slice(1), 10);
    const numB = parseInt(b.slice(1), 10);

    const groupA = numA >= 24 ? 1 : 2;
    const groupB = numB >= 24 ? 1 : 2;

    if (groupA !== groupB) {
      return groupA - groupB;
    }

    return numA - numB;
  };

  const seasonHasData = useMemo(
    () => ({
      current: metricRows.some((row) => row.season === selectedSeason),
      previous: metricRows.some((row) => row.season === previousSeason),
    }),
    [metricRows, selectedSeason, previousSeason]
  );

  // Process data for chart
  const chartData = useMemo(() => {
    if (!metricRows.length) {
      return { series: [], categories: [] };
    }

    const groupAndCalculateMetric = (
      season: string,
      metric: MetricType
    ): Record<string, number> => {
      const result: Record<
        string,
        { totalSales: number; totalVolume: number }
      > = {};

      metricRows.forEach((row) => {
        if (row.season !== season) return;
        if (!row.week_key) return;
        if (selectedVariety !== 'all' && row.variety !== selectedVariety)
          return;

        const key = getGenericWeek(row.week_key);
        if (!key) return;

        if (!result[key]) {
          result[key] = { totalSales: 0, totalVolume: 0 };
        }
        result[key].totalSales += row.total_fob || 0;
        result[key].totalVolume += row.total_kilos || 0;
      });

      const finalResult: Record<string, number> = {};
      for (const key in result) {
        const group = result[key];
        switch (metric) {
          case 'sales':
            finalResult[key] = group.totalSales;
            break;
          case 'volume':
            finalResult[key] = group.totalVolume;
            break;
          case 'fob':
            finalResult[key] =
              group.totalVolume > 0 ? group.totalSales / group.totalVolume : 0;
            break;
        }
      }
      return finalResult;
    };

    const currentData = groupAndCalculateMetric(selectedSeason, selectedMetric);
    const previousData = groupAndCalculateMetric(
      previousSeason,
      selectedMetric
    );

    const allKeysUnsorted = [
      ...new Set([...Object.keys(currentData), ...Object.keys(previousData)]),
    ];
    const allKeys = allKeysUnsorted.sort(sortWeeksCustom);

    const series = [
      {
        name: selectedSeason || 'Temporada Actual',
        data: allKeys.map((key) => currentData[key] || 0),
      },
      {
        name: previousSeason || 'Temporada Anterior',
        data: allKeys.map((key) => previousData[key] || 0),
      },
    ];

    return {
      series,
      categories: allKeys,
    };
  }, [
    metricRows,
    selectedMetric,
    selectedSeason,
    previousSeason,
    selectedVariety,
  ]);

  if (loading) {
    return (
      <Card>
        <CardBody className='p-4 space-y-4'>
          <Skeleton className='h-8 w-1/3 rounded-lg' />
          <Skeleton className='h-10 w-full rounded-lg' />
          <Skeleton className='h-64 w-full rounded-lg' />
        </CardBody>
      </Card>
    );
  }

  if (
    !chartData ||
    !chartData.categories ||
    chartData.categories.length === 0
  ) {
    return (
      <Card>
        <CardBody className='p-4'>
          <div className='text-center p-8'>
            <p className='text-default-400'>
              {t('No hay datos disponibles para comparar las temporadas')}{' '}
              {selectedSeason} {t('y')} {previousSeason}
            </p>
            {!seasonHasData.current && !seasonHasData.previous && (
              <p className='text-xs text-default-300 mt-2'>
                {t(
                  'No se encontraron liquidaciones aprobadas para estas temporadas'
                )}
              </p>
            )}
            {!seasonHasData.current && seasonHasData.previous && (
              <p className='text-xs text-default-300 mt-2'>
                {t('No se encontraron liquidaciones para la temporada')}{' '}
                {selectedSeason}
              </p>
            )}
            {seasonHasData.current && !seasonHasData.previous && (
              <p className='text-xs text-default-300 mt-2'>
                {t('No se encontraron liquidaciones para la temporada')}{' '}
                {previousSeason}
              </p>
            )}
          </div>
        </CardBody>
      </Card>
    );
  }

  const getChartTitle = () => {
    switch (selectedMetric) {
      case 'sales':
        return t('Ventas Totales (USD)');
      case 'volume':
        return t('Volumen Total (kg)');
      case 'fob':
        return t('Precio Promedio FOB (USD/kg)');
    }
  };

  return (
    <Card>
      <CardBody className='p-4'>
        {/* Título y subtítulo */}
        <div className='mb-3'>
          <div className='flex items-center gap-2'>
            <h3 className='text-lg font-bold'>{t('VS Temporada Anterior')}</h3>
            <ChartDownloadButton
              chartRef={chartContainerRef}
              title={t('VS Temporada Anterior')}
              subtitle={`${selectedSeason} ${t('vs')} ${previousSeason} - ${t('Por Semana')}`}
            />
          </div>
          <span className='text-sm text-default-400'>
            {selectedSeason} {t('vs')} {previousSeason} - {t('Por Semana')}
          </span>
        </div>

        {/* Filtros en una fila separada */}
        <div className='flex flex-wrap gap-2 mb-4'>
          <Select
            label={t('Variedad')}
            selectedKeys={selectedVariety ? [selectedVariety] : []}
            onChange={(e) => setSelectedVariety(e.target.value || 'all')}
            className='w-40'
            size='sm'
            aria-label='Selecciona variedad'
            isDisabled={loading}
          >
            <SelectItem key='all' value='all'>
              {t('Todas')}
            </SelectItem>
            {varieties.map((variety) => (
              <SelectItem key={variety} value={variety}>
                {variety}
              </SelectItem>
            ))}
          </Select>

          <Select
            label={t('Métrica')}
            selectedKeys={selectedMetric ? [selectedMetric] : []}
            onChange={(e) => setSelectedMetric(e.target.value as MetricType)}
            className='w-40'
            size='sm'
            aria-label='Selecciona métrica'
          >
            <SelectItem key='sales' value='sales'>
              {t('Ventas Totales')}
            </SelectItem>
            <SelectItem key='volume' value='volume'>
              {t('Volumen Total')}
            </SelectItem>
            <SelectItem key='fob' value='fob'>
              {t('FOB Promedio')}
            </SelectItem>
          </Select>
        </div>

        {chartData.series &&
        chartData.series.length > 0 &&
        chartData.categories.length > 0 ? (
          <div ref={chartContainerRef}>
            <ComparativeBarChart
              series={chartData.series}
              categories={chartData.categories}
              title={getChartTitle()}
              metric={selectedMetric === 'fob' ? 'price' : selectedMetric}
              viewMode='absolute'
            />
          </div>
        ) : (
          <div className='text-center p-8'>
            <p className='text-default-400'>
              {t('Error al procesar los datos del gráfico')}
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default VSTAWidget;
